import {
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ENTITIES, EntityIDMap, ScopeType } from "types";
import React, { useMemo } from "react";
import {
  greenRadioSx,
  permissionsFormLabelSx,
  permissionsHeaderSx,
  redRadioSx,
  yellowRadioSx,
  AUTURA_ASPHALT_LIGHT,
} from "utils";
import { useTranslation } from "react-i18next";

interface PermissionsPanelSkeletonProps {
  entityTypeID: number;
}

const PermissionsPanelSkeleton: React.FC<PermissionsPanelSkeletonProps> = ({
  entityTypeID,
}) => {
  const { t } = useTranslation();
  const mobile = useMediaQuery(useTheme().breakpoints.down("sm"));

  const thirdReadOption: ScopeType | null = useMemo(() => {
    switch (entityTypeID) {
      case EntityIDMap.Job:
        return ScopeType.CreatedByUser;
      case EntityIDMap.Property:
      case EntityIDMap.Property_Group:
        return ScopeType.AssignedToUser;
      default:
        return null;
    }
  }, [entityTypeID]);

  const thirdUpdateOption: ScopeType | null = useMemo(() => {
    switch (entityTypeID) {
      case EntityIDMap.Job:
        return ScopeType.CreatedByUser;
      case EntityIDMap.Property:
      case EntityIDMap.Property_Group:
      case EntityIDMap.User:
        return ScopeType.AssignedToUser;
      default:
        return null;
    }
  }, [entityTypeID]);

  const entityType = useMemo(() => {
    const entity = ENTITIES.find((e) => e.id === entityTypeID);
    return entity?.name || "";
  }, [entityTypeID]);

  return (
    <Grid
      sx={{
        border: "1px solid",
        borderColor: AUTURA_ASPHALT_LIGHT,
        p: 2,
      }}
      size={{
        xs: 12,
        lg: 6,
      }}
    >
      <Stack direction={"column"} spacing={mobile ? 2 : 3} sx={{ pb: 2 }}>
        <Typography
          variant={"h5"}
          sx={permissionsHeaderSx}
          textTransform={"capitalize"}
        >
          {t(`permission.${entityType}.title`)}
        </Typography>
        <FormControl disabled>
          <Stack direction={mobile ? "column" : "row"} display={"flex"}>
            <FormLabel
              id={`${entityType}-read-radio-group`}
              sx={permissionsFormLabelSx}
            >
              <Typography
                variant={"subtitle1"}
                color={"secondary"}
                align={mobile ? "center" : "left"}
              >
                {t(`permission.read`)}
              </Typography>
              {mobile && <Divider variant={"middle"} />}
            </FormLabel>
            <RadioGroup
              aria-labelledby={`${entityType}-read-radio-group`}
              value={ScopeType.None}
              sx={{ flex: 8 }}
            >
              <FormControlLabel
                value={ScopeType.None}
                control={<Radio sx={redRadioSx} />}
                label={t(`permission.${entityType}.read_${ScopeType.None}`)}
                componentsProps={{
                  typography: { variant: mobile ? "subtitle2" : "body1" },
                }}
              />
              {thirdReadOption && (
                <FormControlLabel
                  value={thirdReadOption}
                  control={<Radio sx={greenRadioSx} />}
                  label={t(`permission.${entityType}.read_${thirdReadOption}`)}
                  componentsProps={{
                    typography: { variant: mobile ? "subtitle2" : "body1" },
                  }}
                />
              )}
              <FormControlLabel
                value={ScopeType.Role}
                control={<Radio sx={greenRadioSx} />}
                label={t(`permission.${entityType}.read_${ScopeType.Role}`)}
                componentsProps={{
                  typography: { variant: mobile ? "subtitle2" : "body1" },
                }}
              />
            </RadioGroup>
          </Stack>
        </FormControl>
        <FormControl disabled>
          <Stack direction={mobile ? "column" : "row"} display={"flex"}>
            <FormLabel
              id={`${entityType}-update-radio-group`}
              sx={permissionsFormLabelSx}
            >
              <Typography
                variant={"subtitle1"}
                color={"secondary"}
                align={mobile ? "center" : "left"}
              >
                {t("permission.update")}
              </Typography>
              {mobile && <Divider variant={"middle"} />}
            </FormLabel>
            <RadioGroup
              aria-labelledby={`${entityType}-update-radio-group`}
              value={ScopeType.None}
              sx={{ flex: 8 }}
            >
              <FormControlLabel
                value={ScopeType.None}
                control={<Radio sx={redRadioSx} />}
                label={t(`permission.${entityType}.update_${ScopeType.None}`)}
                componentsProps={{
                  typography: { variant: mobile ? "subtitle2" : "body1" },
                }}
              />
              {thirdUpdateOption && (
                <FormControlLabel
                  value={thirdUpdateOption}
                  control={<Radio sx={yellowRadioSx} />}
                  label={t(
                    `permission.${entityType}.update_${thirdUpdateOption}`,
                  )}
                  componentsProps={{
                    typography: { variant: mobile ? "subtitle2" : "body1" },
                  }}
                />
              )}
              <FormControlLabel
                value={ScopeType.Role}
                control={<Radio sx={greenRadioSx} />}
                label={t(`permission.${entityType}.update_${ScopeType.Role}`)}
                componentsProps={{
                  typography: { variant: mobile ? "subtitle2" : "body1" },
                }}
              />
            </RadioGroup>
          </Stack>
        </FormControl>
        {entityTypeID !== EntityIDMap.Permission && (
          <FormControl disabled>
            <Stack direction={mobile ? "column" : "row"} display={"flex"}>
              <FormLabel
                id={`${entityType}-create-radio-group`}
                sx={permissionsFormLabelSx}
              >
                <Typography
                  variant={"subtitle1"}
                  color={"secondary"}
                  align={mobile ? "center" : "left"}
                >
                  {t("permission.create")}
                </Typography>
                {mobile && <Divider variant={"middle"} />}
              </FormLabel>
              <RadioGroup
                aria-labelledby={`${entityType}-create-radio-group`}
                value={ScopeType.None}
                sx={{ flex: 8 }}
              >
                <FormControlLabel
                  value={ScopeType.None}
                  control={<Radio sx={redRadioSx} />}
                  label={t(`permission.${entityType}.create_${ScopeType.None}`)}
                  componentsProps={{
                    typography: { variant: mobile ? "subtitle2" : "body1" },
                  }}
                />
                <FormControlLabel
                  value={ScopeType.Role}
                  control={<Radio sx={greenRadioSx} />}
                  label={t(`permission.${entityType}.create_${ScopeType.Role}`)}
                  componentsProps={{
                    typography: { variant: mobile ? "subtitle2" : "body1" },
                  }}
                />
              </RadioGroup>
            </Stack>
          </FormControl>
        )}
      </Stack>
    </Grid>
  );
};

export default PermissionsPanelSkeleton;
